
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Users, XCircle } from "lucide-react";
import { useSubscriptionTransactions } from "@/hooks/useSubscriptionTransactions";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

interface Profile {
  id: string;
  username: string;
  avatar_url: string | null;
}

interface SubscriptionsCardProps {
  profiles: Profile[];
}

export const SubscriptionsCard = ({ profiles }: SubscriptionsCardProps) => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const { data: transactions } = useSubscriptionTransactions(userId || undefined);
  
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setUserId(user.id);
    };
    getUser();
  }, []);

  const handleCancelSubscription = async (profileId: string) => {
    try {
      const { error } = await supabase
        .from('subscriptions')
        .delete()
        .eq('subscriber_id', userId)
        .eq('subscribed_to_id', profileId);

      if (error) throw error;
      toast.success("Subscription cancelled successfully");
      // Force a refresh of the profiles list
      window.location.reload();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error("Failed to cancel subscription");
    }
  };

  const totalSpent = transactions?.reduce((sum, t) => sum + t.amount, 0) || 0;

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-blue-500" />
        <h2 className="text-xl font-semibold">My Subscriptions</h2>
      </div>
      <div className="text-sm text-gray-500 mb-4">
        Total spent: ${totalSpent.toFixed(2)}
      </div>
      <div className="space-y-3">
        {profiles.slice(0, 5).map((profile) => (
          <div
            key={profile.id}
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate(`/${profile.username}`)}
            >
              <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden">
                {profile.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={profile.username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-primary text-white">
                    {profile.username.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <span className="font-medium">{profile.username}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={() => handleCancelSubscription(profile.id)}
            >
              <XCircle className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {profiles.length === 0 && (
          <p className="text-gray-500 text-center py-4">
            No subscriptions yet
          </p>
        )}
      </div>
    </Card>
  );
};
